import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Users, Store, ShoppingBag, TrendingUp, IndianRupee } from "lucide-react";

const STATUS_LABELS = { pending: "Pending", accepted: "Accepted", picked_up: "Picked Up", washing: "Washing", ready: "Ready", delivered: "Delivered", cancelled: "Cancelled" };

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/partners").then((r) => setPartners(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="label-eyebrow mb-2">Admin Control Room</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Dhobi Operations</h1>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Stat icon={Users} label="Customers" value={stats.total_customers} color="#1E3A8A" />
            <Stat icon={Store} label="Partners" value={stats.total_partners} color="#C2410C" />
            <Stat icon={ShoppingBag} label="Orders" value={stats.total_orders} color="#7C3AED" />
            <Stat icon={TrendingUp} label="Delivered" value={stats.delivered_orders} color="#059669" />
            <Stat icon={IndianRupee} label="GMV" value={`₹${stats.gmv.toFixed(0)}`} color="#0F172A" />
            <Stat icon={IndianRupee} label="Commission" value={`₹${stats.commission_revenue.toFixed(0)}`} color="#16A34A" />
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto scrollbar-hide">
          {[
            { k: "overview", l: "Orders" },
            { k: "users", l: `Users (${users.length})` },
            { k: "partners", l: `Partners (${partners.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.k ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-muted-foreground"}`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="admin-orders-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr>
                  <Th>Order ID</Th><Th>Customer</Th><Th>Partner</Th><Th>Total</Th><Th>Status</Th><Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <Td className="font-mono text-xs">{o.id.slice(0, 8)}</Td>
                    <Td>{o.customer_name}</Td>
                    <Td>{o.partner_shop_name}</Td>
                    <Td className="font-bold">₹{o.total_amount.toFixed(2)}</Td>
                    <Td><span className="px-2 py-1 rounded text-xs bg-secondary font-semibold">{STATUS_LABELS[o.status]}</span></Td>
                    <Td className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</Td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No orders yet</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>City</Th><Th>Phone</Th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-border">
                    <Td className="font-semibold">{u.name}</Td>
                    <Td>{u.email}</Td>
                    <Td><span className="px-2 py-1 rounded text-xs font-bold uppercase bg-secondary">{u.role}</span></Td>
                    <Td>{u.city || "-"}</Td>
                    <Td>{u.phone || "-"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "partners" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-partners-grid">
            {partners.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-border p-5">
                <div className="font-bold text-lg">{p.shop_name}</div>
                <div className="text-sm text-muted-foreground mb-2">{p.locality}, {p.city}</div>
                <div className="text-xs text-muted-foreground mb-3">{p.description}</div>
                <div className="flex items-center justify-between">
                  <div className="text-sm">⭐ {p.rating} ({p.total_reviews})</div>
                  <div className="font-bold">₹{p.price_per_kg}/kg</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{label}</div>
      <div className="font-display font-black text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left p-3 font-bold text-xs uppercase tracking-wider text-muted-foreground">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`p-3 ${className}`}>{children}</td>;
